import { useForm } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import { toast } from "sonner"
import { Link, useLocation, useSearch } from "wouter"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { api, ApiRequestError } from "@/lib/api"
import { inputClass, submitBtnClass } from "@/lib/styleClasses"
import { AdminMarketingPanel } from "@/components/admin/AdminMarketingPanel"
import { LanguageSwitcher } from "@/components/LanguageSwitcher"

interface RegisterForm {
    name: string
    email: string
    password: string
    acceptedTerms: boolean
    acceptedPrivacy: boolean
}

export const AdminRegisterPage = () => {
    const { t } = useTranslation()
    const [, navigate] = useLocation()
    const search = useSearch()
    const params = new URLSearchParams(search)
    const inviteToken = params.get("invite")
    const prefilledEmail = params.get("email") ?? ""

    const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<RegisterForm>({
        defaultValues: { email: prefilledEmail, acceptedTerms: false, acceptedPrivacy: false },
    })

    const onSubmit = async (data: RegisterForm) => {
        try {
            // Server validates acceptedTerms/acceptedPrivacy as Zod literal(true).
            // RHF guarantees the values are present and true here (checkboxes are
            // required), so we forward the whole payload.
            await api.post("/auth/register", data)
            toast.success(t("admin.register.toastSuccess"))
            const target = inviteToken
                ? `/admin/login?invite=${encodeURIComponent(inviteToken)}`
                : "/admin/login"
            navigate(target)
        } catch (error) {
            const message = error instanceof ApiRequestError ? error.message : t("admin.register.toastFailed")
            toast.error(message)
        }
    }

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
            <div className="w-full max-w-5xl flex justify-end mb-3">
                <LanguageSwitcher />
            </div>
            <div className="flex flex-col lg:flex-row gap-6 lg:items-start w-full max-w-5xl">
            <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-10 w-full max-w-md">
                <div className="mb-8 text-center">
                    <h1 className="text-3xl font-black tracking-tight text-slate-900 mb-2">{t("admin.register.title")}</h1>
                    <p className="text-slate-500 font-bold">{t("admin.register.subtitle")}</p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div className="space-y-1">
                        <label className="text-xs font-black uppercase tracking-widest text-slate-600">
                            {t("admin.register.name")}
                        </label>
                        <Input
                            type="text"
                            placeholder={t("admin.register.namePlaceholder")}
                            className={inputClass}
                            error={errors.name?.message}
                            {...register("name", {
                                required: t("admin.register.nameRequired"),
                                maxLength: { value: 100, message: t("admin.register.nameMax") },
                            })}
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="text-xs font-black uppercase tracking-widest text-slate-600">
                            {t("admin.register.email")}
                        </label>
                        <Input
                            type="email"
                            placeholder="admin@example.com"
                            className={inputClass}
                            error={errors.email?.message}
                            {...register("email", {
                                required: t("admin.register.emailRequired"),
                                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t("admin.register.emailInvalid") },
                            })}
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="text-xs font-black uppercase tracking-widest text-slate-600">
                            {t("admin.register.password")}
                        </label>
                        <Input
                            type="password"
                            placeholder="••••••••"
                            className={inputClass}
                            error={errors.password?.message}
                            {...register("password", {
                                required: t("admin.register.passwordRequired"),
                                minLength: { value: 8, message: t("admin.register.passwordMin") },
                            })}
                        />
                    </div>

                    <div className="space-y-3 pt-2">
                        <label className="flex items-start gap-2 text-sm text-slate-700 font-bold cursor-pointer">
                            <input
                                type="checkbox"
                                className="mt-1 size-4 accent-brand"
                                {...register("acceptedTerms", { required: t("admin.register.acceptRequired") })}
                            />
                            <span>
                                <Trans
                                    i18nKey="admin.register.acceptTerms"
                                    components={{
                                        terms: <a href="/legal/terms" target="_blank" rel="noopener noreferrer" className="text-brand hover:text-brand-dark underline" />,
                                    }}
                                />
                            </span>
                        </label>
                        {errors.acceptedTerms && (
                            <p className="text-xs text-red-500 font-bold ml-6">{errors.acceptedTerms.message}</p>
                        )}

                        <label className="flex items-start gap-2 text-sm text-slate-700 font-bold cursor-pointer">
                            <input
                                type="checkbox"
                                className="mt-1 size-4 accent-brand"
                                {...register("acceptedPrivacy", { required: t("admin.register.acceptRequired") })}
                            />
                            <span>
                                <Trans
                                    i18nKey="admin.register.acceptPrivacy"
                                    components={{
                                        privacy: <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-brand hover:text-brand-dark underline" />,
                                    }}
                                />
                            </span>
                        </label>
                        {errors.acceptedPrivacy && (
                            <p className="text-xs text-red-500 font-bold ml-6">{errors.acceptedPrivacy.message}</p>
                        )}
                    </div>

                    <Button
                        type="submit"
                        disabled={isSubmitting}
                        className={submitBtnClass}
                    >
                        {isSubmitting ? t("admin.register.registering") : t("admin.register.register")}
                    </Button>
                </form>

                <p className="mt-6 text-center text-sm text-slate-500">
                    {t("admin.register.alreadyHaveAccount")}{" "}
                    <Link to="/admin/login" className="font-black text-brand hover:text-brand-dark transition-colors">
                        {t("admin.register.signIn")}
                    </Link>
                </p>
            </div>
            <AdminMarketingPanel />
            </div>
        </div>
    )
}
